import React, { useState, forwardRef, useCallback } from 'react';
import { Eye, EyeSlash, Warning } from '@phosphor-icons/react';
import { Input } from './ui/input';

// ─────────────────────────────────────────────────────────────────────────
// PasswordInput — Champ mot de passe avec :
//   1. Bouton œil pour basculer l'affichage en clair
//   2. Avertissement "Verr. Maj. activé" affiché sous le champ quand
//      Caps Lock est détecté (au focus ou pendant la frappe).
//
// Drop-in replacement de <Input type="password" /> :
//   <PasswordInput value={...} onChange={...} placeholder="..." />
// ─────────────────────────────────────────────────────────────────────────

const PasswordInput = forwardRef(({ className = '', testId, onKeyDown, onKeyUp, onBlur, ...rest }, ref) => {
  const [visible, setVisible] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  const detectCapsLock = useCallback((e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    detectCapsLock(e);
    onKeyDown && onKeyDown(e);
  }, [detectCapsLock, onKeyDown]);

  const handleKeyUp = useCallback((e) => {
    detectCapsLock(e);
    onKeyUp && onKeyUp(e);
  }, [detectCapsLock, onKeyUp]);

  const handleBlur = useCallback((e) => {
    setCapsLockOn(false);
    onBlur && onBlur(e);
  }, [onBlur]);

  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        data-testid={testId}
        className={`pr-10 ${className}`}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        {...rest}
      />
      <button
        type="button"
        data-testid={testId ? `${testId}-toggle` : 'password-toggle'}
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        title={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
      >
        {visible
          ? <EyeSlash size={18} weight="duotone" />
          : <Eye size={18} weight="duotone" />}
      </button>
      {capsLockOn && (
        <p
          data-testid={testId ? `${testId}-capslock-warning` : 'password-capslock-warning'}
          className="mt-1 flex items-center gap-1 text-xs font-semibold text-orange-700"
          role="alert"
        >
          <Warning size={14} weight="duotone" />
          Verrouillage des majuscules activé
        </p>
      )}
    </div>
  );
});

PasswordInput.displayName = 'PasswordInput';

export default PasswordInput;
